import { useState, useRef, useEffect, useCallback } from 'react';

// styles
import {
    MultiDropdownContainer,
    MultiDropdownTrigger,
    MultiDropdownChevron,
    MultiDropdownPanel,
    MultiDropdownOption,
} from './MultiDropdown.styles';

/**
 * @param {string} label
 * @param {{ value: string, label: string }[]} options
 * @param {string[]} selectedValues — controlled; include 'all' to mean "all selected"
 * @param {(next: string[]) => void} onChange
 * @param {import('react').CSSProperties} [style]
 */
export default function MultiDropdown({ label, options = [], selectedValues = ['all'], onChange, style }) {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    const handleOptionClick = useCallback((value) => {
        if (value === 'all') {
            onChange(['all']);
            return;
        }
        const withoutAll = selectedValues.filter(v => v !== 'all');
        const isSelected = withoutAll.includes(value);
        const next = isSelected
            ? withoutAll.filter(v => v !== value)
            : [...withoutAll, value];
        onChange(next.length === 0 ? ['all'] : next);
    }, [selectedValues, onChange]);

    useEffect(() => {
        if (!isOpen) return;
        const handleOutsideClick = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [isOpen]);

    const isAllSelected = selectedValues.includes('all');
    const triggerText = isAllSelected
        ? `${label} (ALL)`
        : `${label} (${selectedValues.length})`;

    return (
        <MultiDropdownContainer ref={containerRef} style={style}>
            <MultiDropdownTrigger $isOpen={isOpen} onClick={() => setIsOpen(v => !v)}>
                {triggerText}
                <MultiDropdownChevron>▾</MultiDropdownChevron>
            </MultiDropdownTrigger>

            {isOpen && (
                <MultiDropdownPanel>
                    <MultiDropdownOption
                        $isSelected={isAllSelected}
                        onClick={() => handleOptionClick('all')}
                    >
                        <input type="checkbox" readOnly checked={isAllSelected} />
                        All
                    </MultiDropdownOption>

                    {options.map(opt => {
                        const isSelected = !isAllSelected && selectedValues.includes(opt.value);
                        return (
                            <MultiDropdownOption
                                key={opt.value}
                                $isSelected={isSelected}
                                onClick={() => handleOptionClick(opt.value)}
                            >
                                <input type="checkbox" readOnly checked={isSelected} />
                                {opt.label}
                            </MultiDropdownOption>
                        );
                    })}
                </MultiDropdownPanel>
            )}
        </MultiDropdownContainer>
    );
}
